import React, { useMemo, useState, useEffect } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import {
  getProductDetails,
  getOneTemplate,
  getAllTemplates,
} from '~/redux/actions/template-builder';
import { SET_PRODUCT_DETAILS } from '~/redux/actions/action-types';
import { fetchCampaigns } from '~/redux/actions/campaign-actions';

// Router
import { useNavigate, NavLink } from 'react-router-dom';

// Components
// import GenericAutocomplete from '~/components/General/AutocompleteNew';
import GenericAutocomplete from '~/components/General/Autocomplete';
import OlcDropdownSearch from '~/components/General/OlcDropdownSearch';
import OrderedProducts from '../MailerDetails/OrderedProducts';

// MUI Components
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Input from '@mui/material/Input';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';

// Images
import { multiPageTemplates } from '~/utils/template-builder';
import {
  HAS_ENVELOPE,
  ENVELOPE_LEARN_URL,
  DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE,
} from '~/utils/constants';
import { PlusIconCIcon, InfoCIcon } from '~/assets/images';
import { createViewProof, createEnvelopeProof } from '~/utils/template-builder';
import { failure } from '~/redux/actions/snackbar-actions';
import { MESSAGES } from '~/utils/message';
import { balanceFormatter } from '~/utils/helperFunctions';
import { fetchUvCoatingPrice } from '~/redux/actions/products-actions';

const MailerDetails = ({
  onValidationChange,
  // Persisted state from parent
  orderType: propOrderType,
  setOrderType: propSetOrderType,
  selectedTemplate: propSelectedTemplate,
  setSelectedTemplate: propSetSelectedTemplate,
  previewTab: propPreviewTab,
  setPreviewTab: propSetPreviewTab,
  selectedPostage: propSelectedPostage,
  setSelectedPostage: propSetSelectedPostage,
  selectedEnvelope: propSelectedEnvelope,
  setSelectedEnvelope: propSetSelectedEnvelope,
  offerPercentageValue: propOfferPercentageValue,
  setOfferPercentageValue: propSetOfferPercentageValue,
  uvCoating: propUvCoating,
  setUvCoating: propSetUvCoating,
  templatePreview: propTemplatePreview,
  setTemplatePreview: propSetTemplatePreview,
  isBackSide: propIsBackSide,
  setIsBackSide: propSetIsBackSide,
  imageLoading: propImageLoading,
  setImageLoading: propSetImageLoading,
  envelopeImage: propEnvelopeImage,
  setEnvelopeImage: propSetEnvelopeImage,
  onSelectionTypeChange,
  onCampaignChange,
  onTemplateChange,
  onPostageChange,
  onEnvelopeChange,
  onEnvelopeImageChange,
  downloadingRows,
  setDownloadingRows,
  setDownloadingProof,
  selectedCampaign,
  setSelectedCampaign,
  campaignSearch,
  setCampaignSearch,
}) => {
  // Use props if provided, otherwise use local state (for backward compatibility)
  const [localOrderType, setLocalOrderType] = useState('single-mailer');
  const [localSelectedTemplate, setLocalSelectedTemplate] = useState(null);
  const [localPreviewTab, setLocalPreviewTab] = useState('mailer');
  const [localSelectedPostage, setLocalSelectedPostage] = useState(null);
  const [localSelectedEnvelope, setLocalSelectedEnvelope] = useState(null);
  const [localOfferPercentageValue, setLocalOfferPercentageValue] = useState('');
  const [localUvCoating, setLocalUvCoating] = useState(false);
  const [uvCoatingPrice, setUvCoatingPrice] = useState(null);
  const [uvCoatingPriceLoading, setUvCoatingPriceLoading] = useState(false);
  const [localTemplatePreview, setLocalTemplatePreview] = useState({
    templateUrl: null,
    backTemplateUrl: null,
  });
  const [selectedTemplateProduct, setSelectedTemplateProduct] = useState(null);
  const [localIsBackSide, setLocalIsBackSide] = useState(false);
  const [localImageLoading, setLocalImageLoading] = useState(false);
  const [localEnvelopeImage, setLocalEnvelopeImage] = useState(null);

  // Use parent state if provided, otherwise fall back to local state
  const orderType = propOrderType !== undefined ? propOrderType : localOrderType;
  const setOrderType = propSetOrderType || setLocalOrderType;
  const selectedTemplate =
    propSelectedTemplate !== undefined ? propSelectedTemplate : localSelectedTemplate;
  const setSelectedTemplate = propSetSelectedTemplate || setLocalSelectedTemplate;
  const previewTab = propPreviewTab !== undefined ? propPreviewTab : localPreviewTab;
  const setPreviewTab = propSetPreviewTab || setLocalPreviewTab;
  const selectedPostage =
    propSelectedPostage !== undefined ? propSelectedPostage : localSelectedPostage;
  const setSelectedPostage = propSetSelectedPostage || setLocalSelectedPostage;
  const selectedEnvelope =
    propSelectedEnvelope !== undefined ? propSelectedEnvelope : localSelectedEnvelope;
  const setSelectedEnvelope = propSetSelectedEnvelope || setLocalSelectedEnvelope;
  const offerPercentageValue =
    propOfferPercentageValue !== undefined ? propOfferPercentageValue : localOfferPercentageValue;
  const setOfferPercentageValue = propSetOfferPercentageValue || setLocalOfferPercentageValue;
  const uvCoating = propUvCoating !== undefined ? propUvCoating : localUvCoating;
  const setUvCoating = propSetUvCoating || setLocalUvCoating;
  const templatePreview =
    propTemplatePreview !== undefined ? propTemplatePreview : localTemplatePreview;
  const setTemplatePreview = propSetTemplatePreview || setLocalTemplatePreview;
  const isBackSide = propIsBackSide !== undefined ? propIsBackSide : localIsBackSide;
  const setIsBackSide = propSetIsBackSide || setLocalIsBackSide;
  const imageLoading = propImageLoading !== undefined ? propImageLoading : localImageLoading;
  const setImageLoading = propSetImageLoading || setLocalImageLoading;
  const envelopeImage = propEnvelopeImage !== undefined ? propEnvelopeImage : localEnvelopeImage;
  const setEnvelopeImage = propSetEnvelopeImage || setLocalEnvelopeImage;

  const handleUvCoatingToggle = () => {
    setUvCoating(!uvCoating);
  };

  const handleUvCoatingToggleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleUvCoatingToggle();
    }
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Templates from selector
  const templates = useSelector((state) => state.templateReducer.templates.rows) || [];
  const products = useSelector((state) => state.productReducers.products) || [];
  const deliveryDuration = useSelector(
    (state) => state.templateReducer.productDetailByTemplate.deliveryDuration
  );
  const deliveryTypes =
    useSelector((state) => state.templateReducer.productDetailByTemplate.deliveryType) || [];
  const envelopeTypes =
    useSelector((state) => state.templateReducer.productDetailByTemplate.envelopeType) || [];
  const campaignsData = useSelector((state) => state.campaignsReducers.campaigns) || [];
  const campaignsLoading = useSelector((state) => state.campaignsReducers.loading) || false;

  // Local search states
  const [templateSearch, setTemplateSearch] = useState('');

  // Map to Autocomplete options { id, label, object }
  const templateOptions = useMemo(() => {
    const base = (templates || []).map((t) => ({
      id: t.id,
      title: `ID:${t.id} ${t.title}`,
      object: t,
    }));
    return base.concat({
      id: '-1',
      title: 'Create New Template',
      custom: true,
      func: () => navigate('/create-template'),
    });
  }, [templates, navigate]);

  const campaignOptions = useMemo(() => {
    const base =
      (campaignsData || [])
        .filter((campaign) => campaign?.templates?.some((template) => template?.id))
        .map((campaign) => ({
          id: campaign.campaignId,
          title: `ID:${campaign.campaignId} ${campaign.name}`,
          ...campaign,
        })) || [];
    return base.concat({
      id: '-1',
      title: 'Create New Campaign',
      custom: true,
      func: () => navigate('/campaigns/create'),
    });
  }, [campaignsData, navigate]);

  // Keep parent informed about selection type for downstream steps
  useEffect(() => {
    if (typeof onSelectionTypeChange === 'function') {
      onSelectionTypeChange(
        orderType === 'campaign' ? { id: 2, label: 'Campaign' } : { id: 1, label: 'Single Mailer' }
      );
    }
  }, [orderType, onSelectionTypeChange]);

  // Propagate selected campaign upwards
  useEffect(() => {
    if (typeof onCampaignChange === 'function') {
      onCampaignChange(selectedCampaign);
    }
  }, [selectedCampaign, onCampaignChange]);

  // Fetch campaigns when switching to campaign mode and when searching
  useEffect(() => {
    if (orderType === 'campaign') {
      dispatch(
        fetchCampaigns({
          pagination: DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE,
        })
      );
    }
  }, [orderType, dispatch]);

  // Template search will be handled directly by onSearchChange promise-returning function

  const handleTemplateSelection = async (option) => {
    setSelectedTemplate(option || null);
    // reset dependent selections when template changes
    setSelectedPostage(null);
    setSelectedEnvelope(null);
    setOfferPercentageValue('');
    setUvCoating(false);
    setUvCoatingPrice(null);
    setTemplatePreview({ templateUrl: null, backTemplateUrl: null });
    setEnvelopeImage(null);
    setIsBackSide(false);
    setPreviewTab('mailer');
    setImageLoading(true);
    setSelectedTemplateProduct(null);
    const templateId = option?.id;
    if (templateId) {
      // First wait for product details to resolve (like old flow), then fetch template preview
      dispatch({ type: SET_PRODUCT_DETAILS, payload: [] });
      await dispatch(getProductDetails({ templateId }));
      const res = await getOneTemplate(templateId);
      const data = res?.data?.data || {};
      const templateUrl = data?.thumbnailUrl || null;
      const backTemplateUrl = data?.backThumbnailUrl || null;
      // keep product details for normalization fallback
      setSelectedTemplateProduct(data?.product || null);
      setTemplatePreview({ templateUrl, backTemplateUrl });
      if (!templateUrl) {
        setImageLoading(false);
      }
    }
  };

  // Promise-returning search handlers for dropdown loader integration
  const searchTemplates = (value) => {
    setTemplateSearch(value);
    if (orderType !== 'single-mailer') return Promise.resolve();
    return dispatch(
      getAllTemplates(
        DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE.page,
        DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE.pageSize,
        value || ''
      )
    );
  };

  const searchCampaigns = (value) => {
    setCampaignSearch(value);
    if (orderType !== 'campaign') return Promise.resolve();
    return dispatch(
      fetchCampaigns({
        pagination: DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE,
        ...(value ? { search: value } : {}),
      })
    );
  };

  const postageOptions = useMemo(() => {
    const sortedDeliveryTypes = [...(deliveryTypes || [])].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );

    return sortedDeliveryTypes.map((type, index) => {
      const label =
        deliveryDuration && deliveryDuration[type] ? `${type} ${deliveryDuration[type]}` : type;
      return { id: String(index), label, object: { value: type } };
    });
  }, [deliveryTypes, deliveryDuration]);

  const envelopeOptions = useMemo(() => {
    return (envelopeTypes || []).map((type, index) => ({
      id: String(index),
      label: type,
      object: { value: type },
    }));
  }, [envelopeTypes]);

  // Require offer percentage if ROS is enabled on template
  const isRosRequired = useMemo(() => {
    return selectedTemplate?.object?.meta?.rosEnabled === true;
  }, [selectedTemplate]);

  const isRosValid = useMemo(() => {
    if (!isRosRequired) return true;
    return (
      offerPercentageValue !== '' &&
      offerPercentageValue !== null &&
      offerPercentageValue !== undefined
    );
  }, [isRosRequired, offerPercentageValue]);

  // If there is only one envelope option, auto-select it
  useEffect(() => {
    if (selectedTemplate && envelopeOptions.length === 1) {
      const onlyOption = envelopeOptions[0];
      if (!selectedEnvelope || selectedEnvelope.label !== onlyOption.label) {
        setSelectedEnvelope(onlyOption);
      }
    }
  }, [selectedTemplate, envelopeOptions, selectedEnvelope]);

  const currentPostageLabel = useMemo(() => {
    // Hide postage label entirely if no template selected
    if (!selectedTemplate) return null;
    if (selectedPostage?.label) return selectedPostage.label;
    if ((postageOptions || []).length > 0) return postageOptions[0].label;
    return null;
  }, [selectedTemplate, selectedPostage, postageOptions]);

  const isPostageDropdownVisible = useMemo(() => {
    if (!selectedTemplate) return false;
    return postageOptions.length > 1;
  }, [selectedTemplate, postageOptions]);

  useEffect(() => {
    if (selectedTemplate && !selectedPostage && postageOptions.length === 1) {
      setSelectedPostage(postageOptions[0]);
    }
  }, [selectedTemplate, selectedPostage, postageOptions, setSelectedPostage]);

  const isUvCoatingVisible = useMemo(() => {
    if (!selectedTemplate) return false;
    return selectedTemplate?.object?.product?.productType === 'Postcards';
  }, [selectedTemplate]);

  const uvCoatingProductId = selectedTemplate?.object?.product?.id || null;

  // Fetch the org's live UV coating rate whenever the selected template resolves to a postcard.
  useEffect(() => {
    if (!isUvCoatingVisible || !uvCoatingProductId) {
      setUvCoatingPrice(null);
      setUvCoatingPriceLoading(false);
      return;
    }
    let cancelled = false;
    setUvCoatingPriceLoading(true);
    dispatch(fetchUvCoatingPrice(uvCoatingProductId)).then((response) => {
      if (cancelled) return;
      const price = response?.data?.data?.uvCoatingPrice;
      setUvCoatingPrice(typeof price === 'number' ? price : null);
      setUvCoatingPriceLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isUvCoatingVisible, uvCoatingProductId, dispatch]);

  // Reset the toggle if the user switches away from a postcard template.
  useEffect(() => {
    if (!isUvCoatingVisible && uvCoating) {
      setUvCoating(false);
    }
  }, [isUvCoatingVisible, uvCoating, setUvCoating]);

  const hasEnvelope = useMemo(() => {
    const productId =
      selectedTemplate?.object?.product?.id ||
      selectedTemplate?.object?.productId ||
      selectedTemplate?.productId;
    return productId ? HAS_ENVELOPE.includes(+productId) : false;
  }, [selectedTemplate]);

  const handleOfferPercentageChange = (e) => {
    const value = e.target.value;
    const regex = /^(100(\.0{1,2})?|[0-9]{1,2}(\.[0-9]{1,2})?)$/;
    if (!isNaN(value) && regex.test(value)) {
      setOfferPercentageValue(value);
    } else if (value === '' || value < 0 || value === 'e') {
      setOfferPercentageValue('');
    }
  };

  // Fetch envelope thumbnail - use template's default or selected envelope's thumbnail
  useEffect(() => {
    if (selectedTemplate && hasEnvelope) {
      let envelopeThumbnail = null;

      // If an envelope style is selected, try to get a specific envelope thumbnail from products.
      // Prefer an exact match (including postage) when available, but fall back to any product
      // that matches the envelope style so the preview updates when the style changes.
      if (selectedEnvelope) {
        let product = null;

        // First try exact match with postage (if selected)
        if (selectedPostage) {
          product = products.find(
            (p) =>
              p.productType === selectedTemplate?.object?.product?.productType &&
              p.deliveryType === selectedPostage?.object?.value &&
              p.paperSize === selectedTemplate?.object?.product?.paperSize &&
              p.envelopeType === selectedEnvelope.label
          );
        }

        // If no exact match found (or postage not selected), fall back to any product
        // that matches template + envelope style so the thumbnail still updates.
        if (!product) {
          product = products.find(
            (p) =>
              p.productType === selectedTemplate?.object?.product?.productType &&
              p.paperSize === selectedTemplate?.object?.product?.paperSize &&
              p.envelopeType === selectedEnvelope.label
          );
        }

        envelopeThumbnail = product?.envelopeThumbnailUrl || null;
      }

      // Fall back to template's default envelope thumbnail if no specific one found
      if (!envelopeThumbnail) {
        envelopeThumbnail = selectedTemplate?.object?.product?.envelopeThumbnailUrl || null;
      }

      setEnvelopeImage(envelopeThumbnail);
    } else {
      setEnvelopeImage(null);
    }
  }, [selectedEnvelope, selectedTemplate, selectedPostage, hasEnvelope, products]);

  // Set loading state when envelope image changes and we're on envelope tab
  useEffect(() => {
    if (previewTab === 'envelope' && envelopeImage) {
      setImageLoading(true);
    }
  }, [envelopeImage, previewTab]);

  // Validation: Check if all required dropdowns are filled
  const isFormValid = useMemo(() => {
    // Campaign flow: require a campaign
    if (orderType === 'campaign') {
      return !!selectedCampaign && selectedCampaign?.id !== '-1';
    }

    // Single Mailer flow: Template is required
    if (!selectedTemplate || selectedTemplate?.id === '-1') {
      return false;
    }

    if (postageOptions.length === 0) {
      return false;
    }

    // Postage is required if postage dropdown is visible and has options
    if (isPostageDropdownVisible && postageOptions.length > 0 && !selectedPostage) {
      return false;
    }

    // Envelope is required if envelope dropdown is visible and has options
    const isEnvelopeDropdownVisible =
      selectedTemplate &&
      !multiPageTemplates.includes(selectedTemplate?.object?.product?.productType);
    if (isEnvelopeDropdownVisible && envelopeOptions.length > 0 && !selectedEnvelope) {
      return false;
    }

    // ROS offer percentage is required if ROS is enabled
    if (!isRosValid) {
      return false;
    }

    return true;
  }, [
    orderType,
    selectedCampaign,
    selectedTemplate,
    selectedPostage,
    selectedEnvelope,
    isPostageDropdownVisible,
    postageOptions,
    envelopeOptions,
    isRosValid,
  ]);

  // Notify parent component about validation state
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isFormValid);
    }
  }, [isFormValid, onValidationChange]);

  // Lift selected postage (normalized) to parent
  useEffect(() => {
    if (typeof onPostageChange === 'function') {
      onPostageChange(
        selectedPostage
          ? {
              value: selectedPostage?.object?.value || selectedPostage?.value,
              label: selectedPostage?.label,
            }
          : null
      );
    }
  }, [selectedPostage, onPostageChange]);

  // Lift selected envelope (normalized) to parent
  useEffect(() => {
    if (typeof onEnvelopeChange === 'function') {
      onEnvelopeChange(selectedEnvelope ? { label: selectedEnvelope?.label } : null);
    }
  }, [selectedEnvelope, onEnvelopeChange]);

  // Lift envelope image to parent (used in Review step)
  useEffect(() => {
    if (typeof onEnvelopeImageChange === 'function') {
      onEnvelopeImageChange(envelopeImage || null);
    }
  }, [envelopeImage, onEnvelopeImageChange]);

  return (
    <Box
      className={`mailerDetailsWapper ${
        orderType === 'campaign' ? 'mailerDetailsWapper--campaign' : ''
      }`.trim()}
    >
      <Box className="selectionPart">
        <Box className="mailerDetailsContent">
          <Typography variant="h4">Order Type</Typography>
          <FormControl className="orderTypeFormControl">
            <RadioGroup
              className="orderTypeRadioGroup"
              name="order-type"
              value={orderType}
              onChange={(e) => setOrderType(e.target.value)}
            >
              <FormControlLabel
                className="orderTypeRadioGroupItem"
                value="single-mailer"
                control={<Radio disableRipple={true} className="orderTypeRadio" />}
                label="Single Mailer"
              />
              <FormControlLabel
                className="orderTypeRadioGroupItem"
                value="campaign"
                control={<Radio disableRipple={true} className="orderTypeRadio" />}
                label="Campaign"
              />
            </RadioGroup>
          </FormControl>
        </Box>
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {orderType === 'single-mailer' && (
            <Box className="mailerDetailsContent">
              <Typography variant="h4">Select Template</Typography>
              <Box className="mailerDetailsField">
                <OlcDropdownSearch
                  options={templateOptions}
                  selectedValue={selectedTemplate}
                  setSelectedValue={handleTemplateSelection}
                  clearable={true}
                  placeHolderText={'Select Template'}
                  noOptionText="No options found"
                  component="multipleIds"
                  searchAutocomplete
                  searchFieldPlaceholderText={'Search Template'}
                  onSearchChange={searchTemplates}
                  menuPaperClassName="createOrderDropdownMenu"
                  className={selectedTemplate ? 'activeState' : 'inactiveState'}
                  activeState={true}
                />
                {currentPostageLabel && !isPostageDropdownVisible && (
                  <Typography className="postageTypeText">
                    Postage Type: {currentPostageLabel}
                  </Typography>
                )}

                {selectedTemplate &&
                  !multiPageTemplates.includes(selectedTemplate?.object?.product?.productType) &&
                  envelopeOptions.length === 1 && (
                    <Typography className="postageTypeText">
                      Envelope Style: {selectedEnvelope?.label || envelopeOptions[0]?.label}
                    </Typography>
                  )}
              </Box>
            </Box>
          )}
          {orderType === 'single-mailer' && selectedTemplate && (
            <>
              {isPostageDropdownVisible && (
                <Box className="mailerDetailsContent" sx={{ gap: '0px !important' }}>
                  <Typography variant="h6">
                    Postage Type
                    <span className="required">*</span>
                  </Typography>
                  <Box className="mailerDetailsField">
                    <GenericAutocomplete
                      placeholder="Postage Type"
                      options={postageOptions}
                      setSelectedValue={setSelectedPostage}
                      selectedValue={selectedPostage}
                      search={() => {}}
                      updateErrors={() => {}}
                      // name={'postage'}
                      newDesign={true}
                      className="newAutocompleteDesign"
                      menuClassName="createOrderDropdownMenu"
                      readOnlyInput={true}
                      // hideClearIcon={true}
                      // menuSearch
                      // disableMainInput
                      // noOptionText="No postage types found"
                    />
                  </Box>
                </Box>
              )}
              {/* {isUvCoatingVisible && (
                <Box className="mailerDetailsContent" sx={{ gap: '0px !important' }}>
                  <Box className="uvCoatingControl">
                    <Box className="uvCoatingCopy">
                      <Typography variant="h6" className="uvCoatingLabel">
                        Add UV coating
                      </Typography>
                      {uvCoatingPriceLoading ? (
                        <CircularProgress className="uvCoatingPriceLoader" size={14} />
                      ) : uvCoatingPrice != null && (
                        <Typography className="uvCoatingPriceBadge">
                          +{balanceFormatter(uvCoatingPrice)}/pc
                        </Typography>
                      )}
                      <Tooltip
                        disableInteractive
                        title="A high-gloss UV coating on the front for a premium, eye-catching finish that resists scuffing in the mail."
                        placement="top"
                      >
                        <button
                          type="button"
                          className="uvCoatingInfoButton"
                          aria-label="Learn more about UV coating"
                        >
                          <img src={InfoCIcon} alt="" aria-hidden="true" />
                        </button>
                      </Tooltip>
                    </Box>
                    <Box
                      className={`uvCoatingToggleWrapper ${uvCoating ? 'enabled' : ''}`}
                      onClick={handleUvCoatingToggle}
                      onKeyDown={handleUvCoatingToggleKeyDown}
                      role="switch"
                      tabIndex={0}
                      aria-checked={uvCoating}
                      aria-label="Add UV coating"
                    >
                      <Box className="toggle-track">
                        <Box className="toggle-thumb" />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )} */}
              {!multiPageTemplates.includes(selectedTemplate?.object?.product?.productType) && (
                <>
                  {envelopeOptions.length > 1 && (
                    <Box className="mailerDetailsContent" sx={{ gap: '0px !important' }}>
                      <Typography variant="h6">
                        Envelope Style
                        <span className="required">*</span>
                      </Typography>
                      <Box className="mailerDetailsField">
                        <GenericAutocomplete
                          placeholder="Select Envelope"
                          options={envelopeOptions}
                          setSelectedValue={setSelectedEnvelope}
                          selectedValue={selectedEnvelope}
                          search={() => {}}
                          updateErrors={() => {}}
                          name={'envelope'}
                          newDesign={true}
                          className="newAutocompleteDesign"
                          menuClassName="createOrderDropdownMenu"
                          // hideClearIcon={true}
                          // menuSearch
                          // disableMainInput
                          // noOptionText="No envelope styles found"
                        />
                      </Box>
                    </Box>
                  )}
                </>
              )}
              {selectedTemplate?.object?.meta?.rosEnabled === true && (
                <Box className="mailerDetailsContent" sx={{ gap: '0px !important' }}>
                  <Typography variant="h6">
                    Enter Offer Percentage
                    <span className="required">*</span>
                  </Typography>
                  <Box className="mailerDetailsField">
                    <Input
                      className="offerPercentageInput"
                      type="number"
                      placeholder="Enter number between 0-100"
                      sx={{ width: '100%' }}
                      value={offerPercentageValue}
                      onChange={handleOfferPercentageChange}
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                      }}
                    />
                    {/* {!isRosValid && (
                      <Typography className="error" sx={{ fontSize: '12px', mt: '6px' }}>
                        <sup>*</sup>
                        This field is required
                      </Typography>
                    )} */}
                    <span className="offerPercentageText">
                      Unsure of your offer percentage?{' '}
                      <span className="offerPercentageLink">
                        <NavLink to="/property-offers" target="_blank">
                          Click here
                        </NavLink>
                      </span>
                    </span>
                  </Box>
                </Box>
              )}
            </>
          )}
          {orderType === 'campaign' && (
            <Box className="mailerDetailsContent">
              <Typography variant="h4">Select Campaign</Typography>
              <Box className="mailerDetailsField">
                <OlcDropdownSearch
                  options={campaignOptions}
                  selectedValue={selectedCampaign}
                  setSelectedValue={setSelectedCampaign}
                  clearable={true}
                  placeHolderText={'Select Campaign'}
                  noOptionText="No options found"
                  component="multipleIds"
                  searchFieldPlaceholderText={'Search Campaign'}
                  onSearchChange={searchCampaigns}
                  searchLoading={campaignsLoading}
                  menuPaperClassName="createOrderDropdownMenu"
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {orderType === 'single-mailer' && selectedTemplate?.id && (
        <Box
          className="previewPart"
          style={{
            display: !hasEnvelope ? 'flex' : '',
            alignItems: !hasEnvelope ? 'center' : 'flex-start',
          }}
        >
          {hasEnvelope && (
            <Box className="previewTabs">
              <Box
                role="button"
                tabIndex={0}
                className={`previewTab ${previewTab === 'mailer' ? 'active' : ''}`}
                onClick={() => {
                  setPreviewTab('mailer');
                  setImageLoading(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setPreviewTab('mailer');
                    setImageLoading(true);
                  }
                }}
              >
                <Typography>Mailer Proof</Typography>
              </Box>

              <Box
                role="button"
                tabIndex={0}
                className={`previewTab ${previewTab === 'envelope' ? 'active' : ''}`}
                onClick={() => {
                  setPreviewTab('envelope');
                  if (envelopeImage) {
                    setImageLoading(true);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setPreviewTab('envelope');
                    if (envelopeImage) {
                      setImageLoading(true);
                    }
                  }
                }}
              >
                <Typography>Envelope Proof</Typography>
              </Box>
            </Box>
          )}
          <Box className="previewPartContent" sx={{ position: 'relative' }}>
            {previewTab === 'mailer' ? (
              <>
                {!templatePreview.templateUrl ? (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <CircularProgress
                      sx={{
                        color: '#ed5c2f',
                      }}
                    />
                  </Box>
                ) : (
                  <>
                    <img
                      src={
                        isBackSide && templatePreview.backTemplateUrl
                          ? templatePreview.backTemplateUrl
                          : templatePreview.templateUrl
                      }
                      alt="preview"
                      onLoad={() => setImageLoading(false)}
                      onError={() => setImageLoading(false)}
                      style={{
                        display: imageLoading ? 'none' : 'block',
                      }}
                    />
                    {imageLoading && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <CircularProgress
                          sx={{
                            color: '#ed5c2f',
                          }}
                        />
                      </Box>
                    )}
                    {templatePreview.backTemplateUrl ? (
                      <>
                        {/* Disable left arrow on front; enable on back */}
                        <Box
                          role="button"
                          aria-disabled={!isBackSide}
                          tabIndex={isBackSide ? 0 : -1}
                          className={`leftArrowIcon ${!isBackSide ? 'disableArrow' : ''}`}
                          sx={{
                            pointerEvents: isBackSide ? 'auto' : 'none',
                            opacity: isBackSide ? 1 : 0.5,
                          }}
                          onClick={() => {
                            if (!isBackSide) return;
                            setImageLoading(true);
                            setIsBackSide(false);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && isBackSide) {
                              setImageLoading(true);
                              setIsBackSide(false);
                            }
                          }}
                        />
                        {/* Disable right arrow on back; enable on front */}
                        <Box
                          role="button"
                          aria-disabled={isBackSide}
                          tabIndex={!isBackSide ? 0 : -1}
                          className={`rightArrowIcon ${isBackSide ? 'disableArrow' : ''}`}
                          sx={{
                            pointerEvents: !isBackSide ? 'auto' : 'none',
                            opacity: !isBackSide ? 1 : 0.5,
                          }}
                          onClick={() => {
                            if (isBackSide) return;
                            if (templatePreview.backTemplateUrl) {
                              setImageLoading(true);
                              setIsBackSide(true);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !isBackSide) {
                              setImageLoading(true);
                              setIsBackSide(true);
                            }
                          }}
                        />
                      </>
                    ) : null}
                  </>
                )}
              </>
            ) : previewTab === 'envelope' ? (
              <>
                {!envelopeImage ? (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <CircularProgress
                      sx={{
                        color: '#ed5c2f',
                      }}
                    />
                  </Box>
                ) : (
                  <>
                    <img
                      key={`${envelopeImage}-${selectedEnvelope?.label || 'default'}`}
                      src={envelopeImage}
                      alt="envelope preview"
                      onLoad={() => {
                        setImageLoading(false);
                      }}
                      onError={() => {
                        setImageLoading(false);
                      }}
                      style={{
                        display: imageLoading ? 'none' : 'block',
                      }}
                    />
                    {imageLoading && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <CircularProgress
                          sx={{
                            color: '#ed5c2f',
                          }}
                        />
                      </Box>
                    )}
                  </>
                )}
              </>
            ) : null}
          </Box>
        </Box>
      )}
      {orderType === 'campaign' && selectedCampaign && (
        <Box className="campaignProductsPanel">
          <OrderedProducts
            selectedCampaign={selectedCampaign}
            downloadViewProof={async (currentIndex = null) => {
              try {
                setDownloadingRows((prev) => [...prev, { index: currentIndex, type: 'mailer' }]);
                setDownloadingProof(true);
                const label = selectedCampaign?.templates?.[currentIndex]?.label;
                const templateId = +selectedCampaign?.templates?.[currentIndex]?.id;
                await dispatch(createViewProof(label, templateId));
              } catch (error) {
                dispatch(failure(MESSAGES.VIEW_PROOF_ERROR));
              } finally {
                setDownloadingProof(false);
                setDownloadingRows((prev) =>
                  prev.filter((row) => !(row.index === currentIndex && row.type === 'mailer'))
                );
              }
            }}
            downloadEnvelopeProof={async (currentIndex = null) => {
              try {
                setDownloadingRows((prev) => [...prev, { index: currentIndex, type: 'envelope' }]);
                setDownloadingProof(true);
                const selectedCampaignTemplate = selectedCampaign?.templates?.[currentIndex];
                const payload = {};
                payload.productId =
                  products.find(
                    (product) =>
                      product.productType === selectedCampaignTemplate?.productType &&
                      product.deliveryType === selectedCampaignTemplate?.selectedPostage?.value &&
                      product.paperSize === selectedCampaignTemplate?.postCardSize &&
                      (selectedCampaignTemplate?.selectedEnvelope
                        ? product.envelopeType === selectedCampaignTemplate?.selectedEnvelope?.label
                        : true)
                  )?.id || selectedCampaignTemplate?.productId;
                await dispatch(createEnvelopeProof(selectedCampaignTemplate?.label, payload));
              } catch (error) {
                dispatch(failure(MESSAGES.VIEW_PROOF_ERROR));
              } finally {
                setDownloadingProof(false);
                setDownloadingRows((prev) =>
                  prev.filter((row) => !(row.index === currentIndex && row.type === 'envelope'))
                );
              }
            }}
            downloadingRows={downloadingRows}
          />
        </Box>
      )}
    </Box>
  );
};

export default MailerDetails;
